import React, { useEffect, useState } from 'react';
import { Row, Col, Panel, Loader } from 'rsuite';
import * as images from '@/images/charts';
import './styles.less';
import MapClusters from './MapClusters.jsx';
import DataTable from './DataTable';
import { useTranslation } from 'react-i18next';

import {
  getExpensesByEngagementCode,
  getExpensesBySL,
  getMissionsBetweenDates
  // getCurrencies
} from '@/services/mission.service';
import { format } from 'date-fns';
import PieChart from './PieChart';
import Chart from 'react-apexcharts';
import { useAppDispatch, useAppSelector } from '@/config/hooks';

const Dashboard = props => {
  const { t } = useTranslation();
  const { dates } = props;

  const [expensesBySl, setExpensesBySL] = useState([]);
  const [expensesByEngagementCode, setExpenseByEngagementCode] = useState([]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    const fetchData = async () => {
      if (dates?.length > 0) {
        const resSl = await getExpensesBySL(
          format(dates[0], 'yyyy-MM-dd'),
          format(dates[1], 'yyyy-MM-dd')
        );
        setExpensesBySL(resSl);

        const resEngagementCode = await getExpensesByEngagementCode(
          format(dates[0], 'yyyy-MM-dd'),
          format(dates[1], 'yyyy-MM-dd')
        );
        setExpenseByEngagementCode(resEngagementCode);
      }
    };

    if (dates?.length > 0) {
      dispatch(
        getMissionsBetweenDates(format(dates[0], 'yyyy-MM-dd'), format(dates[1], 'yyyy-MM-dd'))
      );
      fetchData();
    }
  }, [dates]);

  const missions = useAppSelector((state: any) => state.dashboard.data);
  const loading = useAppSelector((state: any) => state.missions.loading);

  const [dataArray, setDataArray] = useState([]);
  const [departures, setDepartures] = useState(0);
  const [ongoing, setOngoing] = useState(0);
  const [returns, setReturns] = useState(0);

  useEffect(() => {
    setDataArray(missions);
    setDepartures(missions.filter((mission: any) => mission.missionTag === 'departure')?.length);
    setOngoing(missions.filter((mission: any) => mission.missionTag === 'ongoing')?.length);
    setReturns(missions.filter((mission: any) => mission.missionTag === 'returning')?.length);
  }, [missions]);

  return (
    <>
      {loading && <Loader backdrop content="Fetching data..." vertical style={{ zIndex: 9999 }} />}
      <Row gutter={30} className="dashboard-header">
        <Col xs={8}>
          <Panel className="trend-box bg-gradient-red">
            <img className="chart-img-travel" src={images.processingIcon} />
            <div className="title">{t('Departures')}</div>
            <div className="value">{departures}</div>
            <div className="description">{t('Missions Set to Depart')}</div>
          </Panel>
        </Col>
        <Col xs={8}>
          <Panel className="trend-box bg-gradient-green">
            <img className="chart-img-travel" src={images.onGoingIcon} />
            <div className="title">{t('Ongoing')} </div>
            <div className="value">{ongoing}</div>
            <div className="description">{t('Currently Active Missions')}</div>
          </Panel>
        </Col>
        <Col xs={8}>
          <Panel className="trend-box bg-gradient-blue">
            <img className="chart-img-travel" src={images.returnIcon} />
            <div className="title">{t('Returns')}</div>
            <div className="value">{returns}</div>
            <div className="description">{t('Missions Set to Return')}</div>
          </Panel>
        </Col>
      </Row>

      <Row gutter={30}>
        <MapClusters />
      </Row>

      <Row gutter={30}>
        <DataTable
          title={
            t("Mission Overview for Selected Period: Ongoing, Departures, Returns, and Departure-Return Missions")
        }
          data={dataArray}
        />
      </Row>

      <Row className="mt-10">
        <Col xs={4}>
          <PieChart
            title="ServiceLine Expenses TND"
            data={expensesBySl?.map(item => item.totalTND)}
            type="pie"
            labels={expensesBySl?.map(item => item.serviceLineName)}
            height={300}
          />
        </Col>

        <Col xs={4}>
          <PieChart
            title="ServiceLine Expenses EUR"
            data={expensesBySl?.map(item => item.totalEUR)}
            type="pie"
            labels={expensesBySl?.map(item => item.serviceLineName)}
            height={300}
          />
        </Col>

        <Col md={16}>
          <Panel
            className="card"
            style={{ height: 450 }}
            bodyFill
            header={'EURO costs per serviceline'}
          >
            <Chart
              series={[
                {
                  name: t('Perdiem'),
                  data: expensesBySl?.map(item => item.totalPerdiemSum?.toFixed(2))
                },
                {
                  name: t('Extra expenses'),
                  data: expensesBySl?.map(item => item.totalExtraExpenses?.toFixed(2))
                },
                {
                  name: t('Accomodation'),
                  data: expensesBySl?.map(item => item.totalAccomodation?.toFixed(2))
                },
                {
                  name: t('Transportation'),
                  data: expensesBySl?.map(item => item.totalTransportationEUR?.toFixed(2))
                }
              ]}
              type={'bar'}
              height={350}
              options={{
                chart: {
                  type: 'bar',
                  height: 350,
                  stacked: true,
                  toolbar: {
                    show: true
                  },
                  zoom: {
                    enabled: true
                  }
                },
                responsive: [
                  {
                    breakpoint: 480,
                    options: {
                      legend: {
                        position: 'bottom',
                        offsetX: -10,
                        offsetY: 0
                      }
                    }
                  }
                ],
                plotOptions: {
                  bar: {
                    horizontal: false,
                    borderRadius: 10,
                    borderRadiusApplication: 'end', // 'around', 'end'
                    borderRadiusWhenStacked: 'last', // 'all', 'last'
                    dataLabels: {
                      total: {
                        enabled: true,
                        style: {
                          fontSize: '13px',
                          fontWeight: 900
                        }
                      }
                    }
                  }
                },
                xaxis: {
                  type: 'category',
                  categories: expensesBySl?.map(item => item.serviceLineName)
                },
                legend: {
                  position: 'right',
                  offsetY: 40
                },
                fill: {
                  opacity: 1
                }
              }}
            />
          </Panel>
        </Col>
      </Row>

      <Row gutter={30} className="mt-10">
        <Panel
          className="card"
          style={{ height: 400, overflow: 'hidden' }} // Ensures no scroll on the Panel itself
          bodyFill
          header={'Euro Costs by Engagement Code'}
        >
          <div style={{ overflowX: 'auto', width: '100%', height: '100%' }}>
            <div style={{ minWidth: Math.max(expensesByEngagementCode?.length * 60, 800) }}>
              <Chart
                series={[
                  {
                    name: t('Cost'),
                    data: expensesByEngagementCode?.map(item =>
                      (item.totalEUR?.$numberDecimal || item.totalEUR)?.toFixed(2)
                    )
                  }
                ]}
                type={'bar'}
                height={350}
                options={{
                  dataLabels: {
                    enabled: false
                  },
                  chart: {
                    type: 'bar',
                    height: 350,
                    stacked: true,
                    toolbar: {
                      show: true
                    },
                    zoom: {
                      enabled: true
                    }
                  },
                  responsive: [
                    {
                      breakpoint: 480,
                      options: {
                        legend: {
                          position: 'bottom',
                          offsetX: -10,
                          offsetY: 0
                        }
                      }
                    }
                  ],
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      borderRadius: 10,
                      borderRadiusApplication: 'end',
                      borderRadiusWhenStacked: 'last',
                      dataLabels: {
                        total: {
                          enabled: true,
                          style: {
                            fontSize: '13px',
                            fontWeight: 900
                          }
                        }
                      }
                    }
                  },
                  xaxis: {
                    type: 'category',
                    categories: expensesByEngagementCode?.map(item => item.engagementCode)
                  },
                  legend: {
                    position: 'right',
                    offsetY: 40
                  },
                  fill: {
                    opacity: 1
                  }
                }}
              />
            </div>
          </div>
        </Panel>
      </Row>

      <Row gutter={30} className="mt-10">
        <Panel
          className="card"
          style={{ height: 400 }}
          bodyFill
          header={'TND Costs by Engagement Code'}
        >
          <div style={{ overflowX: 'auto', width: '100%', height: '100%' }}>
            <div style={{ minWidth: Math.max(expensesByEngagementCode?.length * 60, 800) }}>
              <Chart
                series={[
                  {
                    name: t('Cost'),
                    data: expensesByEngagementCode?.map(item => item.totalTND?.toFixed(2))
                  }
                ]}
                type={'bar'}
                height={350}
                options={{
                  dataLabels: {
                    enabled: false
                  },
                  chart: {
                    type: 'bar',
                    height: 350,
                    stacked: true,
                    toolbar: {
                      show: true
                    },
                    zoom: {
                      enabled: true
                    }
                  },
                  colors: ['#FFA07A'],
                  responsive: [
                    {
                      breakpoint: 480,
                      options: {
                        legend: {
                          position: 'bottom',
                          offsetX: -10,
                          offsetY: 0
                        }
                      }
                    }
                  ],
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      borderRadius: 10,
                      borderRadiusApplication: 'end', // 'around', 'end'
                      borderRadiusWhenStacked: 'last', // 'all', 'last'
                      dataLabels: {
                        total: {
                          enabled: true,
                          style: {
                            fontSize: '13px',
                            fontWeight: 900
                          }
                        }
                      }
                    }
                  },
                  xaxis: {
                    type: 'category',
                    categories: expensesByEngagementCode?.map(item => item.engagementCode)
                  },
                  legend: {
                    position: 'right',
                    offsetY: 40
                  },
                  fill: {
                    opacity: 1
                  }
                }}
              />
            </div>
          </div>
        </Panel>
      </Row>
    </>
  );
};

export default Dashboard;
